import { useState, useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { PromptSlider } from "./PromptSlider";
import { PromptInput, PromptInputHandle } from "./PromptInput";
import { SuggestionChips } from "./SuggestionChips";
import { getPromptColor } from "@/lib/prompt-colors";
import { WeightedPrompt, MusicConfig } from "@/lib/lyria-client";
import { PromptMode } from "@/hooks/use-lyria";
import { toast } from "sonner";

const PROMPT_TRANSLATION_TIMEOUT_MS = 30000;

export interface PromptSlidersHandle {
  /** Flush any pending text from the input field before playing */
  flushInput: () => Promise<string | null>;
}

interface PromptSlidersProps {
  prompts: WeightedPrompt[];
  onSetPromptsWithMode: (prompts: WeightedPrompt[], mode: PromptMode) => void;
  onUpdateConfig?: (config: Partial<MusicConfig>, needsReset?: boolean) => void;
  onPrimeAudio?: () => void;
  onPlay?: () => void;
  autoFocus?: boolean;
}

export const PromptSliders = forwardRef<PromptSlidersHandle, PromptSlidersProps>(
  function PromptSliders({ prompts, onSetPromptsWithMode, onUpdateConfig, onPrimeAudio, onPlay, autoFocus }, ref) {
    const inputRef = useRef<PromptInputHandle>(null);
    const inFlightRef = useRef(false);
    const promptListRef = useRef(prompts);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
      promptListRef.current = prompts;
    }, [prompts]);

    useImperativeHandle(ref, () => ({
      flushInput: async () => {
        if (inFlightRef.current) return null;
        return inputRef.current?.flush() ?? null;
      },
    }), []);

    const handleWeightChange = useCallback(
      (index: number, weight: number) => {
        const updated = prompts.map((p, i) =>
          i === index ? { ...p, weight } : p
        );
        promptListRef.current = updated;
        onSetPromptsWithMode(updated, "replace");
      },
      [prompts, onSetPromptsWithMode]
    );

    const handleDelete = useCallback(
      (index: number) => {
        const updated = prompts.filter((_, i) => i !== index);
        promptListRef.current = updated;
        onSetPromptsWithMode(updated, "replace");
      },
      [prompts, onSetPromptsWithMode]
    );

    const addSafePrompt = useCallback(
      (text: string) => {
        const updated = [...promptListRef.current, { text, weight: 1.0 }];
        promptListRef.current = updated;
        onSetPromptsWithMode(updated, "replace");
      },
      [onSetPromptsWithMode]
    );

    // Route free-text input through the chat-ai worker for translation + splitting
    const handleAddFromInput = useCallback(
      async (text: string): Promise<boolean> => {
        if (inFlightRef.current) return false;
        inFlightRef.current = true;
        onPrimeAudio?.();
        setIsProcessing(true);
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), PROMPT_TRANSLATION_TIMEOUT_MS);

        try {
          const workerUrl = import.meta.env.VITE_WORKER_URL || "http://localhost:8787";
          const response = await fetch(`${workerUrl}/chat-ai`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userMessage: text,
              variant: "focused",
              conversationHistory: [],
              currentContext: {
                isPlaying: false,
                currentPrompts: prompts,
              },
            }),
            signal: controller.signal,
          });

          if (!response.ok) throw new Error(`AI request failed: ${response.status}`);
          if (!response.body) throw new Error("AI response stream was empty");

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          const processSseLine = (line: string): "success" | "failure" | "skip" => {
            if (!line.startsWith("data: ")) return "skip";
            let event: { type: string; data?: Record<string, unknown> };
            try {
              event = JSON.parse(line.slice(6));
            } catch {
              return "skip";
            }
            if (event.type === "error") {
              throw new Error("AI processing error");
            }
            if (event.type !== "result" || !event.data) return "skip";

            const aiPrompts = event.data.prompts as WeightedPrompt[] | undefined;
            if (!Array.isArray(aiPrompts) || aiPrompts.length === 0) {
              toast.error("AI translation returned no playable prompt");
              return "failure";
            }

            const mode = (event.data.promptMode as PromptMode) || "replace";
            onSetPromptsWithMode(aiPrompts, mode);

            const suggestedConfig = event.data.suggestedConfig as Partial<MusicConfig> | undefined;
            if (suggestedConfig && onUpdateConfig) {
              const needsReset = suggestedConfig.bpm !== undefined || suggestedConfig.scale !== undefined;
              onUpdateConfig(suggestedConfig, needsReset);
            }
            if (onPlay) {
              onPlay();
            }
            return "success";
          };

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              const result = processSseLine(line);
              if (result === "success") return true;
              if (result === "failure") return false;
            }
          }
          buffer += decoder.decode();
          for (const line of buffer.split("\n")) {
            const result = processSseLine(line.trim());
            if (result === "success") return true;
            if (result === "failure") return false;
          }
          // Stream ended without a result event
          toast.error("AI translation ended before creating a prompt");
          return false;
        } catch (error) {
          console.error("[PromptSliders] AI translation failed:", error);
          if (error instanceof DOMException && error.name === "AbortError") {
            toast.error("AI translation timed out. Prompt was not sent to Lyria.");
          } else {
            toast.error("AI translation failed. Prompt was not sent to Lyria.");
          }
          return false;
        } finally {
          window.clearTimeout(timeoutId);
          inFlightRef.current = false;
          setIsProcessing(false);
        }
      },
      [prompts, onSetPromptsWithMode, onUpdateConfig, onPrimeAudio, onPlay]
    );

    const handleClearAll = useCallback(() => {
      promptListRef.current = [];
      onSetPromptsWithMode([], "replace");
    }, [onSetPromptsWithMode]);

    return (
      <div className="space-y-4">
        {/* Active prompt sliders */}
        {prompts.length > 0 && (
          <div className="space-y-1">
            {prompts.map((prompt, i) => (
              <PromptSlider
                key={`${prompt.text}-${i}`}
                text={prompt.text}
                weight={prompt.weight}
                color={getPromptColor(i)}
                onWeightChange={(w) => handleWeightChange(i, w)}
                onDelete={() => handleDelete(i)}
              />
            ))}
          </div>
        )}

        {/* Add prompt input */}
        <PromptInput
          ref={inputRef}
          onAdd={handleAddFromInput}
          onClearAll={handleClearAll}
          hasPrompts={prompts.length > 0}
          disabled={isProcessing}
          autoFocus={autoFocus}
        />

        {/* Suggestion chips */}
        <SuggestionChips onSelect={addSafePrompt} />
      </div>
    );
  }
);
